import {
  isDev,
  isStaging,
  isProduction,
  isNonProduction,
  getEnvironmentConfig,
} from "./common";

/**
 * Feature flags based on environment
 */
export interface FeatureFlags {
  // Development and debugging features
  enableReactQueryDevtools: boolean;
  enableRouterDevtools: boolean;
  enableDetailedLogging: boolean;

  // API and external services
  enableSentry: boolean;
  enableAnalytics: boolean;
  enableHotReload: boolean;

  // UI features
  showDebugInfo: boolean;
  enableExperimentalFeatures: boolean;
  showBetaFeatures: boolean;
  enableTestingMode: boolean;

  // Security and privacy
  enableSourceMaps: boolean;
  enableConsoleLogging: boolean;
  strictMode: boolean;
  basketRebalance: boolean;
}

/**
 * Get feature flags based on current environment
 */
export function getFeatureFlags(): FeatureFlags {
  const config = getEnvironmentConfig();

  return {
    // Development and debugging features
    enableReactQueryDevtools: config.enableDevtools,
    enableRouterDevtools: config.enableDevtools,
    enableDetailedLogging: config.debugMode,

    // API and external services
    enableSentry: config.sentryEnabled,
    enableAnalytics: isProduction(),
    enableHotReload: isDev(),

    // UI features
    showDebugInfo: isNonProduction(),
    enableExperimentalFeatures: isDev(),
    showBetaFeatures: isStaging() || isDev(),
    enableTestingMode: isNonProduction(),

    // Security and privacy
    enableSourceMaps: isNonProduction(),
    enableConsoleLogging: isNonProduction(),
    strictMode: isProduction(),
    basketRebalance: isNonProduction(),
  };
}

/**
 * Check if a specific feature is enabled
 */
export function isFeatureEnabled(feature: keyof FeatureFlags): boolean {
  const flags = getFeatureFlags();
  return flags[feature];
}

/**
 * Conditional execution based on environment
 */
export function ifDev<T>(callback: () => T): T | undefined {
  return isDev() ? callback() : undefined;
}

export function ifStaging<T>(callback: () => T): T | undefined {
  return isStaging() ? callback() : undefined;
}

export function ifProduction<T>(callback: () => T): T | undefined {
  return isProduction() ? callback() : undefined;
}

export function ifNonProduction<T>(callback: () => T): T | undefined {
  return isNonProduction() ? callback() : undefined;
}

/**
 * Environment-aware console logging
 */
export const envLogger = {
  debug: (...args: unknown[]) => {
    if (isFeatureEnabled("enableConsoleLogging")) {
      console.debug("[DEBUG]", ...args);
    }
  },

  info: (...args: unknown[]) => {
    if (isFeatureEnabled("enableConsoleLogging")) {
      console.info("[INFO]", ...args);
    }
  },

  warn: (...args: unknown[]) => {
    if (isFeatureEnabled("enableConsoleLogging") || isProduction()) {
      console.warn("[WARN]", ...args);
    }
  },

  error: (...args: unknown[]) => {
    console.error("[ERROR]", ...args);
  },

  table: (data: unknown) => {
    if (isFeatureEnabled("enableConsoleLogging")) {
      console.table(data);
    }
  },
};
